import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';
import { 
  GraduationCap, 
  ArrowLeft,
  UserCheck,
  AlertTriangle,
  Ban,
  Heart
} from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

const AcceptableUsePage = () => {
  return (
    <div className="min-h-screen bg-stone-50" data-testid="acceptable-use-page">
      {/* Header */}
      <header className="bg-white border-b border-stone-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </Link>
              <Link to="/" className="flex items-center gap-2">
                <GraduationCap className="h-8 w-8 text-indigo-700" />
                <span className="font-heading text-xl sm:text-2xl font-semibold text-slate-900">
                  Gifted Academy Tutors
                </span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-12 md:py-16 px-6 md:px-12 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp} className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-2xl bg-teal-100 flex items-center justify-center">
              <UserCheck className="h-8 w-8 text-teal-700" />
            </div>
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-medium text-slate-900">
                Acceptable Use Policy
              </h1>
              <p className="text-slate-500">Last updated: March 2026</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp} className="prose prose-slate max-w-none">
            <div className="bg-white rounded-2xl border border-stone-200 p-8 md:p-12 space-y-8">
              
              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  1. Purpose
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  This Acceptable Use Policy outlines the expected behavior and conduct for all users of the Gifted Academy Tutors platform. Our goal is to maintain a safe, respectful, and productive learning environment for everyone.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  2. Respectful Behavior
                </h2>
                <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                  <div className="flex items-start gap-3">
                    <Heart className="h-5 w-5 text-green-600 mt-1 flex-shrink-0" />
                    <p className="text-slate-700 leading-relaxed">
                      <strong>Users must behave respectfully toward tutors and staff.</strong> This includes using appropriate language, being punctual, and maintaining professional conduct during all interactions.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  3. Prohibited Conduct
                </h2>
                <div className="bg-red-50 rounded-xl p-6 border border-red-200 mb-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-600 mt-1 flex-shrink-0" />
                    <p className="text-slate-700 leading-relaxed">
                      <strong>Harassment, abuse, or inappropriate behavior will result in account suspension.</strong>
                    </p>
                  </div>
                </div>

                <p className="text-slate-600 leading-relaxed mb-4">
                  The following behaviors are strictly prohibited:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Verbal abuse, threats, or intimidation toward tutors, staff, or other users</li>
                  <li>Sexual harassment or inappropriate comments</li>
                  <li>Discriminatory language or behavior based on race, gender, religion, nationality, or any protected characteristic</li>
                  <li>Bullying or persistent negative behavior</li>
                  <li>Disruptive behavior that interferes with tutoring sessions</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  4. Session Recording Prohibition
                </h2>
                <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
                  <div className="flex items-start gap-3">
                    <Ban className="h-5 w-5 text-amber-600 mt-1 flex-shrink-0" />
                    <p className="text-slate-700 leading-relaxed">
                      <strong>Students may not record tutoring sessions.</strong> This includes video recording, audio recording, screen recording, or any other method of capturing session content without explicit written permission from Gifted Academy Tutors.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  5. Content Restrictions
                </h2>
                <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                  <p className="text-slate-700 leading-relaxed">
                    <strong>Users may not share illegal, offensive, or harmful content</strong> through the platform.
                  </p>
                </div>

                <p className="text-slate-600 leading-relaxed mt-4 mb-4">
                  This includes but is not limited to:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Copyrighted material without permission</li>
                  <li>Malware, viruses, or malicious code</li>
                  <li>Pornographic or sexually explicit content</li>
                  <li>Violent or graphic content</li>
                  <li>Content promoting illegal activities</li>
                  <li>Spam or unsolicited commercial content</li>
                  <li>False or misleading information</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  6. Academic Integrity
                </h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Gifted Academy Tutors promotes academic integrity. Users must not:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Request tutors to complete assignments or exams on their behalf</li>
                  <li>Submit tutor-provided work as their own without proper attribution</li>
                  <li>Share exam questions or answers in violation of academic policies</li>
                  <li>Engage in any form of academic dishonesty</li>
                </ul>
                <p className="text-slate-600 leading-relaxed mt-4">
                  Our tutors are here to help you learn and understand concepts, not to do your work for you.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  7. Platform Misuse
                </h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Users must not misuse the platform in any way, including:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Attempting to circumvent security measures</li>
                  <li>Creating multiple accounts to evade restrictions</li>
                  <li>Impersonating another person or entity</li>
                  <li>Using the platform for non-educational commercial purposes</li>
                  <li>Interfering with the platform's operation or other users' experience</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  8. Consequences of Violations
                </h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Violations of this Acceptable Use Policy may result in:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li><strong>Warning:</strong> For minor or first-time violations</li>
                  <li><strong>Temporary Suspension:</strong> For repeated or moderate violations</li>
                  <li><strong>Permanent Ban:</strong> For serious or repeated violations</li>
                  <li><strong>Legal Action:</strong> For violations involving illegal activity</li>
                </ul>
                <p className="text-slate-600 leading-relaxed mt-4">
                  Gifted Academy Tutors reserves the right to take action at its sole discretion without prior notice in cases of serious violations.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  9. Reporting Violations
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  If you witness or experience any violation of this policy, please report it to us immediately at:
                </p>
                <p className="text-indigo-700 font-medium mt-2">
                  contact@giftedacademytutors.ca
                </p>
                <p className="text-slate-600 leading-relaxed mt-4">
                  All reports will be handled confidentially and investigated promptly.
                </p>
              </div>

            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AcceptableUsePage;
